/**
 * VendorACHPanel — Org-wide Vendor ACH activity view.
 * ──────────────────────────────────────────────────────
 * Two tabs:
 *   1. Bills            — every bill across every PF (filter by ACH status)
 *   2. Payment Files    — every PF rolled up with SFTP transmission status
 *                         and synthesised bank response
 *
 * Mounted at /payments/vendor-ach.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import {
  AlertCircle, Banknote, CheckCircle2, Clock, Eye,
  Download, FileText, Filter, Loader2, RefreshCw, Send, ShieldCheck, X, XCircle,
} from 'lucide-react';

import api from '../../lib/api';
import MainLayout from '../../components/MainLayout';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '../../components/ui/sheet';

const fmtMoney = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
};
const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const ACH_STATUS_CFG = {
  transmitted: { cls: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: CheckCircle2, label: 'Transmitted' },
  transmit_failed: { cls: 'bg-rose-100 text-rose-800 border-rose-200', icon: XCircle, label: 'Failed' },
  queued: { cls: 'bg-amber-100 text-amber-800 border-amber-200', icon: Clock, label: 'Queued' },
  ach_eligible: { cls: 'bg-sky-100 text-sky-800 border-sky-200', icon: Banknote, label: 'ACH eligible' },
  not_ach: { cls: 'bg-slate-100 text-slate-700 border-slate-200', icon: null, label: 'Non-ACH' },
};

const PAYMENT_STATUS_CFG = {
  paid: { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'Paid' },
  approved: { cls: 'bg-sky-50 text-sky-700 border-sky-200', label: 'Approved' },
  review: { cls: 'bg-amber-50 text-amber-700 border-amber-200', label: 'Review' },
};

function getParams() {
  try {
    const a = JSON.parse(localStorage.getItem('current_account') || 'null');
    return a?.id ? { account_id: a.id } : {};
  } catch { return {}; }
}

function BankResponsePill({ resp }) {
  if (!resp) return <span className="text-xs text-muted-foreground">—</span>;
  const map = {
    accepted: { cls: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: CheckCircle2 },
    rejected: { cls: 'bg-rose-100 text-rose-800 border-rose-200', icon: XCircle },
    skipped: { cls: 'bg-slate-100 text-slate-700 border-slate-200', icon: AlertCircle },
    pending: { cls: 'bg-amber-100 text-amber-800 border-amber-200', icon: Clock },
    unknown: { cls: 'bg-slate-100 text-slate-700 border-slate-200', icon: null },
  };
  const m = map[resp.status] || map.unknown;
  const Icon = m.icon;
  return (
    <div className="flex flex-col items-start gap-0.5">
      <Badge variant="outline" className={`text-xs ${m.cls} rounded-none`}>
        {Icon && <Icon className="h-3 w-3 mr-1" />}{resp.label}
      </Badge>
      <span className="text-[11px] text-muted-foreground truncate max-w-[280px]" title={resp.message}>
        {resp.message}
      </span>
    </div>
  );
}

// ─── Bills tab ───────────────────────────────────────────────────────────
function BillsTab() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [achOnly, setAchOnly] = useState(false);

  // Invoice viewer side-sheet — opens the bill's PDF when the user
  // clicks the eye icon in the Action column.
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [invoiceBill, setInvoiceBill] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfBase64, setPdfBase64] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfMessage, setPdfMessage] = useState(null);

  const openInvoice = async (bill) => {
    setInvoiceBill(bill);
    setInvoiceOpen(true);
    setPdfLoading(true);
    setPdfBase64(null);
    setPdfUrl(null);
    setPdfMessage(null);
    try {
      const acctId = bill.account_id
        || (JSON.parse(localStorage.getItem('current_account') || 'null') || {}).id;
      const { data } = await api.get(`/payments/v2/bill-pdf/${bill.bill_id}`, {
        params: { account_id: acctId },
      });
      if (data.base64_data) setPdfBase64(data.base64_data);
      else if (data.pdf_url) setPdfUrl(data.pdf_url);
      else setPdfMessage(data.message || 'No PDF available for this bill');
    } catch (e) {
      setPdfMessage(e.response?.data?.detail || 'Failed to load PDF');
    } finally {
      setPdfLoading(false);
    }
  };
  const pdfDataUrl = pdfBase64 ? `data:application/pdf;base64,${pdfBase64}` : null;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        ...getParams(),
        limit: 1000,
        ach_only: achOnly,
        ...(statusFilter !== 'all' ? { payment_status: statusFilter } : {}),
      };
      const { data } = await api.get('/vendor-ach-panel/bills', { params });
      setRows(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load bills');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, achOnly]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      (r._dn_supplier_name || '').toLowerCase().includes(q)
      || (r.invoice_number || '').toLowerCase().includes(q)
      || (r.pf_batch_name || '').toLowerCase().includes(q)
      || (r.transaction_reference || '').toLowerCase().includes(q)
    );
  }, [rows, query]);

  return (
    <div className="space-y-4" data-testid="vach-bills-tab">
      <div className="flex flex-wrap gap-3 items-center">
        <Input
          data-testid="vach-bills-search"
          placeholder="Search vendor, invoice #, PF, txn ref…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="rounded-none max-w-sm"
        />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="rounded-none w-44" data-testid="vach-bills-status">
            <Filter className="h-3 w-3 mr-1" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All payment status</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="review">In Review</SelectItem>
          </SelectContent>
        </Select>
        <Button
          variant={achOnly ? 'default' : 'outline'}
          size="sm"
          className="rounded-none"
          onClick={() => setAchOnly((v) => !v)}
          data-testid="vach-bills-ach-only"
        >
          <Banknote className="h-3 w-3 mr-1" /> ACH only
        </Button>
        <span className="text-xs text-muted-foreground ml-auto">
          Showing {filtered.length} of {total.toLocaleString()} bills
        </span>
        <Button variant="ghost" size="sm" onClick={load} className="rounded-none">
          <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      <div className="border border-border rounded-none overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/30">
              <TableHead className="whitespace-nowrap">Bill #</TableHead>
              <TableHead>Vendor</TableHead>
              <TableHead>Property</TableHead>
              <TableHead>Invoice #</TableHead>
              <TableHead>Invoice Date</TableHead>
              <TableHead>Due Date</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>PF Ref</TableHead>
              <TableHead>Txn Ref</TableHead>
              <TableHead>ACH Status</TableHead>
              <TableHead>Payment Status</TableHead>
              <TableHead className="text-center">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && [...Array(8)].map((_, i) => (
              <TableRow key={`sk-${i}`}>
                <TableCell colSpan={12}><Skeleton className="h-6 w-full" /></TableCell>
              </TableRow>
            ))}
            {!loading && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={12} className="text-center py-10 text-sm text-muted-foreground">
                  No bills match the current filters.
                </TableCell>
              </TableRow>
            )}
            {!loading && filtered.map((r) => {
              const achCfg = ACH_STATUS_CFG[r.ach_status] || ACH_STATUS_CFG.not_ach;
              const psCfg = PAYMENT_STATUS_CFG[r.payment_status] || { cls: 'bg-slate-50 text-slate-700 border-slate-200', label: r.payment_status || '—' };
              const AchIcon = achCfg.icon;
              const amt = r.approved_amount || r.payment_amount || r.current_bill_amount || 0;
              return (
                <TableRow key={r.bill_id} data-testid={`vach-bill-row-${r.bill_id}`}>
                  <TableCell className="font-mono text-xs">{(r.bill_id || '').slice(0, 8)}…</TableCell>
                  <TableCell className="text-sm font-medium">{r._dn_supplier_name || '—'}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{r._dn_property_name || '—'}</TableCell>
                  <TableCell className="font-mono text-xs">{r.invoice_number || '—'}</TableCell>
                  <TableCell className="text-xs">{fmtDate(r.invoice_date)}</TableCell>
                  <TableCell className="text-xs">{fmtDate(r.due_date)}</TableCell>
                  <TableCell className="text-right font-mono text-sm">{fmtMoney(amt)}</TableCell>
                  <TableCell>
                    {r.pf_batch_name ? (
                      <Link to={`/payments/v2/${r.payment_file_id}`} className="text-sky-700 hover:underline text-xs font-mono">
                        {r.pf_batch_name}
                      </Link>
                    ) : '—'}
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    {r.transaction_reference ? (
                      <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-emerald-700">
                        {r.transaction_reference}
                      </span>
                    ) : '—'}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`text-xs ${achCfg.cls} rounded-none`}>
                      {AchIcon && <AchIcon className="h-3 w-3 mr-1" />}{achCfg.label}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`text-xs ${psCfg.cls} rounded-none`}>
                      {psCfg.label}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-center">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2"
                      onClick={() => openInvoice(r)}
                      title="View bill PDF"
                      data-testid={`vach-bill-view-${r.bill_id}`}
                    >
                      <Eye className="h-3.5 w-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Invoice Viewer side-sheet — shows the bill's PDF inline */}
      <Sheet open={invoiceOpen} onOpenChange={setInvoiceOpen}>
        <SheetContent
          side="right"
          className="sm:max-w-[50%] w-[50%] flex flex-col p-0"
          data-testid="vach-invoice-viewer"
        >
          <SheetHeader className="px-6 pt-6 pb-3 border-b border-border">
            <SheetTitle className="flex items-start justify-between gap-2 text-sm">
              <div className="flex flex-col gap-0.5 min-w-0">
                <span className="font-semibold">Invoice Viewer</span>
                <span className="text-xs font-normal text-muted-foreground truncate">
                  {invoiceBill ? (
                    <>
                      {invoiceBill._dn_supplier_name || 'Vendor'}
                      {invoiceBill.invoice_number && ` • ${invoiceBill.invoice_number}`}
                      {invoiceBill.bill_id && ` • ${invoiceBill.bill_id}`}
                    </>
                  ) : '—'}
                </span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0 shrink-0"
                onClick={() => setInvoiceOpen(false)}
                data-testid="vach-invoice-viewer-close"
              >
                <X className="h-4 w-4" />
              </Button>
            </SheetTitle>
          </SheetHeader>
          <div className="flex-1 min-h-0 bg-muted/20">
            {pdfLoading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : (pdfDataUrl || pdfUrl) ? (
              <iframe
                src={pdfDataUrl || pdfUrl}
                title="Invoice PDF"
                className="w-full h-full border-0"
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                <FileText className="h-12 w-12 mb-3 opacity-40" />
                <p className="text-sm font-medium">
                  {pdfMessage || 'No PDF available'}
                </p>
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

// ─── Payment Files tab ────────────────────────────────────────────────────
function PaymentFilesTab() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [achOnly, setAchOnly] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/vendor-ach-panel/payment-files', {
        params: { ...getParams(), limit: 500, ach_only: achOnly },
      });
      setRows(data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load payment files');
    } finally {
      setLoading(false);
    }
  }, [achOnly]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      (r.batch_name || '').toLowerCase().includes(q)
      || (r.created_by_name || '').toLowerCase().includes(q)
      || (r.latest_nacha_filename || '').toLowerCase().includes(q)
    );
  }, [rows, query]);

  const retry = async (batchId) => {
    try {
      await api.post(`/vendor-ach/batches/${batchId}/transmit`, {}, { params: getParams() });
      toast.success('SFTP retry triggered');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Retry failed');
    }
  };

  const download = async (batchId, filename) => {
    try {
      const resp = await api.get(`/vendor-ach/batches/${batchId}/download`, {
        params: getParams(), responseType: 'blob',
      });
      const blob = new Blob([resp.data], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename || `${batchId}.ach`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Download failed');
    }
  };

  return (
    <div className="space-y-4" data-testid="vach-pf-tab">
      <div className="flex flex-wrap gap-3 items-center">
        <Input
          data-testid="vach-pf-search"
          placeholder="Search PF #, filename, created by…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="rounded-none max-w-sm"
        />
        <Button
          variant={achOnly ? 'default' : 'outline'}
          size="sm"
          className="rounded-none"
          onClick={() => setAchOnly((v) => !v)}
          data-testid="vach-pf-ach-only"
        >
          <Banknote className="h-3 w-3 mr-1" /> Only PFs with ACH batches
        </Button>
        <span className="text-xs text-muted-foreground ml-auto">{filtered.length} payment files</span>
        <Button variant="ghost" size="sm" onClick={load} className="rounded-none">
          <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      <div className="border border-border rounded-none overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/30">
              <TableHead>PF #</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Vendors Paid</TableHead>
              <TableHead className="text-right">Total Approved</TableHead>
              <TableHead className="text-right">Total Payment</TableHead>
              <TableHead>SFTP Transmissions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && [...Array(6)].map((_, i) => (
              <TableRow key={`sk-${i}`}>
                <TableCell colSpan={6}><Skeleton className="h-6 w-full" /></TableCell>
              </TableRow>
            ))}
            {!loading && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-10 text-sm text-muted-foreground">
                  No payment files to show.
                </TableCell>
              </TableRow>
            )}
            {!loading && filtered.map((r) => (
              <TableRow key={r.id} data-testid={`vach-pf-row-${r.id}`} className="align-top">
                <TableCell>
                  <button
                    onClick={() => navigate(`/payments/v2/${r.id}`)}
                    className="text-sky-700 hover:underline font-mono text-xs"
                  >
                    {r.batch_name || r.id.slice(0, 8)}
                  </button>
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className="rounded-none text-xs capitalize">
                    {r.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-right font-mono text-sm">
                  {r.vendors_paid || 0}/{r.total_vendors || 0}
                </TableCell>
                <TableCell className="text-right font-mono text-sm">
                  {fmtMoney(r.total_approved_amount)}
                </TableCell>
                <TableCell className="text-right font-mono text-sm">
                  {fmtMoney(r.total_payment_amount)}
                </TableCell>
                <TableCell className="py-2" data-testid={`vach-pf-transmissions-${r.id}`}>
                  {Array.isArray(r.batches) && r.batches.length > 0 ? (
                    <div className="space-y-1.5">
                      {r.batches.map((b, i) => {
                        const st = (b.sftp_status || '').toLowerCase();
                        const canRetry = b.id && (st === 'failed' || st === 'pending' || st === 'skipped');
                        return (
                          <div
                            key={b.id || i}
                            className="flex flex-wrap items-center gap-2 border-b border-border/40 last:border-0 pb-1.5 last:pb-0"
                            data-testid={`vach-pf-transmission-${r.id}-${i}`}
                          >
                            {b.ach_format ? (
                              <Badge variant="outline" className="rounded-none text-[10px] bg-violet-50 text-violet-700 border-violet-200">
                                {b.ach_format}
                              </Badge>
                            ) : null}
                            <span className="text-xs text-muted-foreground whitespace-nowrap">
                              {fmtDateTime(b.sftp_uploaded_at || b.created_at)}
                            </span>
                            <BankResponsePill resp={b.bank_response} />
                            {b.id && b.nacha_filename && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="rounded-none h-6 px-1.5"
                                title="Download NACHA file"
                                onClick={() => download(b.id, b.nacha_filename)}
                                data-testid={`vach-pf-download-${r.id}-${i}`}
                              >
                                <Download className="h-3 w-3" />
                              </Button>
                            )}
                            {canRetry && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="rounded-none h-6 px-1.5 text-amber-700 hover:bg-amber-50"
                                title="Retry SFTP upload"
                                onClick={() => retry(b.id)}
                                data-testid={`vach-pf-retry-${r.id}-${i}`}
                              >
                                <Send className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <span className="text-xs text-muted-foreground">Not sent</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ─── Page shell ────────────────────────────────────────────────────────────
export default function VendorACHPanel() {
  const [tab, setTab] = useState('bills');
  return (
    <MainLayout>
      <div className="container mx-auto px-6 py-8" data-testid="vendor-ach-panel">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
              <ShieldCheck className="h-7 w-7 text-emerald-600" /> Vendor ACH
            </h1>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
              Org-wide visibility into vendor ACH payments — every bill across
              every payment file, with SFTP transmission status and bank-response
              acknowledgement. Use the tabs below to switch between Bills and
              Payment Files views.
            </p>
          </div>
          <Link to="/settings#sftp-config" className="text-xs text-sky-700 hover:underline flex items-center gap-1 shrink-0">
            <FileText className="h-3 w-3" /> SFTP settings
          </Link>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="rounded-none border-b">
            <TabsTrigger value="bills" data-testid="vach-tab-bills" className="rounded-none">
              Bills
            </TabsTrigger>
            <TabsTrigger value="pfs" data-testid="vach-tab-pfs" className="rounded-none">
              Payment Files
            </TabsTrigger>
          </TabsList>
          <TabsContent value="bills" className="mt-6">
            <BillsTab />
          </TabsContent>
          <TabsContent value="pfs" className="mt-6">
            <PaymentFilesTab />
          </TabsContent>
        </Tabs>
      </div>
    </MainLayout>
  );
}
